import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Sparkles, Bell, AlertTriangle, Inbox, Smartphone, ShieldCheck, Palette,
  TrendingUp, ArrowDownToLine, Wallet, FileSpreadsheet, KeyRound, Rocket, Send, Eye,
} from 'lucide-react';
import { APP_VERSION, CHANGELOG } from '../changelog';

// Mapping nome stringa -> componente icona (per tenere changelog.js serializzabile)
const ICON_MAP = {
  Bell, AlertTriangle, Sparkles, Inbox, Smartphone, ShieldCheck, Palette,
  TrendingUp, ArrowDownToLine, Wallet, FileSpreadsheet, KeyRound, Rocket, Send, Eye,
};

const SEEN_KEY = 'last_seen_version';

export const hasUnseenVersion = () => {
  try {
    const seen = localStorage.getItem(SEEN_KEY);
    return seen !== APP_VERSION;
  } catch {
    return false;
  }
};

export const markVersionSeen = () => {
  try { localStorage.setItem(SEEN_KEY, APP_VERSION); } catch { /* ignore */ }
};

/**
 * WhatsNewModal — mostra le novità dell'app.
 *
 * Props:
 *  - open: boolean controllato dal parent
 *  - onClose: callback alla chiusura
 *  - user: oggetto user (se super_admin, non mostriamo mai il modal)
 *
 * Logica aggiuntiva: se `autoOpen` è true, si apre automaticamente al primo render
 * quando `localStorage['last_seen_version']` è diverso dalla versione corrente.
 */
const WhatsNewModal = ({ open, onClose, autoOpen = false, user = null }) => {
  const [internalOpen, setInternalOpen] = useState(false);

  useEffect(() => {
    if (!autoOpen) return;
    if (user && user.role === 'super_admin') return; // super admin: niente auto-popup
    if (hasUnseenVersion()) {
      // Piccolo delay per non collidere con altri popup (toast rinnovo appare a 4s)
      const t = setTimeout(() => setInternalOpen(true), 1200);
      return () => clearTimeout(t);
    }
  }, [autoOpen, user]);

  // Il parent può controllare l'apertura passando `open`, ma anche l'auto-open
  // interno (via useEffect) deve poter aprire il dialog. Perciò facciamo OR
  // tra lo stato esterno (manuale) e quello interno (auto).
  const isOpen = !!open || internalOpen;

  const handleClose = () => {
    markVersionSeen();
    setInternalOpen(false);
    onClose?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="whats-new-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <div className="relative">
              <Sparkles className="w-6 h-6 text-amber-500" />
            </div>
            <span>Novità di DDT Manager</span>
            <Badge variant="secondary" className="ml-auto font-mono text-xs" data-testid="whats-new-version">
              v{APP_VERSION}
            </Badge>
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground pt-1">
            Scopri cosa è cambiato in questa versione dell'app.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {CHANGELOG.map((release, idx) => (
            <div
              key={release.version}
              data-testid={`changelog-release-${release.version}`}
              className={idx === 0 ? '' : 'opacity-80 border-t border-border pt-4'}
            >
              <div className="flex items-baseline gap-2 mb-1.5">
                <h3 className="text-sm font-semibold font-['JetBrains_Mono'] text-foreground">
                  v{release.version}
                </h3>
                <span className="text-[11px] text-muted-foreground">· {release.date}</span>
                {idx === 0 && (
                  <Badge className="ml-auto bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">
                    attuale
                  </Badge>
                )}
              </div>
              {release.title && (
                <p className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground mb-2">
                  {release.title}
                </p>
              )}
              <ul className="space-y-2">
                {release.highlights.map((h, i) => {
                  const Icon = ICON_MAP[h.icon] || Rocket;
                  return (
                    <li key={i} className="flex gap-2.5 text-sm">
                      <Icon className="w-4 h-4 flex-shrink-0 mt-0.5 text-primary" />
                      <span className="leading-snug text-foreground/90">{h.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button onClick={handleClose} data-testid="whats-new-close-btn" className="w-full sm:w-auto">
            Ho capito, grazie
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsNewModal;
